// Unpublished Work © 2025-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {MultiSelect} from '@deere/isg.component-library';

function PartsMultiSelect(props) {
    const {
        binLocations = [],
        inventoryId,
        fetchBinLocations,
        currencyPreference,
        disabled,
        filteredParts,
        addPartFooterAction,
        hideBinLocationInput,
        hideFinancialData,
        inventoryParts,
        onPartSelect,
        partsInputComponent: PartsInputComponent,
        readOnly,
        selectedParts,
        setPartRequestsToRemove,
        setSelectedParts,
        setValid,
        translations,
        selectedPartsClassName
    } = props;

    function onChange(name, value, partToChange) {
        partToChange[name] = value;

        setSelectedParts([...selectedParts]);
    }

    function onClear(part) {
        const filteredSelectedParts = selectedParts.filter(({id}) => id !== part.id);

        setSelectedParts(filteredSelectedParts);
    }

    const selectedPartComponents = React.useMemo(() => selectedParts.map((part) => (
        <PartsInputComponent
            binLocations={binLocations}
            currencyPreference={currencyPreference}
            disabled={disabled}
            fetchBinLocations={fetchBinLocations}
            hideBinLocationInput={hideBinLocationInput}
            hideFinancialData={hideFinancialData}
            inventoryId={inventoryId}
            inventoryParts={inventoryParts}
            key={part.id}
            onChange={onChange}
            onClear={onClear}
            part={part}
            readOnly={readOnly}
            setPartRequestsToRemove={setPartRequestsToRemove}
            setValid={setValid}
            translations={translations}
        />
    )), [selectedParts]);

    const partLabel = React.useMemo(() => selectedParts.length > 0 ? (
        <div className='part-area-label'>
            {translations.ONLINK_PART}
        </div>
    ) : null, [selectedParts]);

    return (
        <div
            className='parts-multi-select'
            data-testid='parts-multi-select'
        >
            {partLabel}
            <div className={selectedPartsClassName}>
                {selectedPartComponents}
            </div>
            {
                !readOnly &&
                <div className='settings-group mobile'>
                    <MultiSelect
                        autoFocus={true}
                        disabled={disabled}
                        enableVirtualization
                        footerAction={addPartFooterAction}
                        items={filteredParts}
                        label={translations.ONLINK_ADD_PART}
                        labels={{
                            emptyList: translations.ONLINK_NO_PARTS,
                            placeholder: translations.ONLINK_PARTS_SEARCH
                        }}
                        onChange={onPartSelect}
                        selectedIds={[]}
                        single={true}
                    />
                </div>
            }
        </div>
    );
}

PartsMultiSelect.propTypes = {
    addPartFooterAction: PropTypes.object,
    binLocations: PropTypes.arrayOf(PropTypes.object),
    currencyPreference: PropTypes.string,
    disabled: PropTypes.bool,
    fetchBinLocations: PropTypes.func,
    filteredParts: PropTypes.arrayOf(PropTypes.object),
    hideBinLocationInput: PropTypes.bool,
    hideFinancialData: PropTypes.bool,
    inventoryId: PropTypes.string,
    inventoryParts: PropTypes.arrayOf(PropTypes.object),
    onPartSelect: PropTypes.func,
    partsInputComponent: PropTypes.elementType,
    readOnly: PropTypes.bool,
    selectedParts: PropTypes.arrayOf(PropTypes.object),
    selectedPartsClassName: PropTypes.string,
    setPartRequestsToRemove: PropTypes.func,
    setSelectedParts: PropTypes.func,
    setValid: PropTypes.func,
    translations: PropTypes.translations
};

export default PartsMultiSelect;
